<script setup lang="ts">
  import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import InfoIcon from '@/shared/components/icons/InfoIcon.vue'
  import { arrow, autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'

  interface Emits {
    (e: 'openTooltip'): void
  }

  interface Slots {
    'main-content'?: () => void
    button?: (props: { toggleTooltip: () => void }) => void
    'tooltip-content'?: () => void
  }

  interface Props {
    label?: string
    amount?: string
    info?: string | string[]
    size?: number
    color?: string
    tooltipTimeout?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    label: 'label',
    amount: 'amount',
    info: 'info',
    size: 24,
    color: '#394B5A',
    tooltipTimeout: 200, // seconds
  })
  const emit = defineEmits<Emits>()
  defineSlots<Slots>()

  const showTooltip = ref(false)
  const timer = ref<null | NodeJS.Timeout>(null)

  const btnTooltipEl = ref<HTMLElement | null>(null)
  const floatingEl = ref<HTMLElement | null>(null)
  const arrowEl = ref<HTMLElement | null>(null)

  let cleanupAutoUpdate: () => void

  const updatePosition = async () => {
    if (!btnTooltipEl.value || !floatingEl.value || !arrowEl.value) return

    const { x, y, placement, middlewareData } = await computePosition(
      btnTooltipEl.value,
      floatingEl.value,
      {
        middleware: [
          offset(8),
          flip({ fallbackPlacements: ['top', 'bottom'] }),
          shift({ padding: 12 }),
          arrow({ element: arrowEl.value }),
        ],
      }
    )

    Object.assign(floatingEl.value.style, {
      position: 'absolute',
      transform: `translate(${x}px, ${y}px)`,
      left: '0',
      top: '0',
    })

    const arrowData = middlewareData.arrow
    if (arrowData) {
      const staticSideMap = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }

      const side = placement.split('-')[0]
      const staticSide = staticSideMap[side as keyof typeof staticSideMap]

      Object.assign(arrowEl.value.style, {
        left: arrowData.x !== null ? `${arrowData.x}px` : '',
        top: arrowData.y !== null ? `${arrowData.y}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-5px',
      })
    }
  }

  onClickOutside(floatingEl, () => {
    showTooltip.value = false
  })

  const toggleTooltip = () => (showTooltip.value = !showTooltip.value)

  watch(showTooltip, async (value) => {
    if (value) {
      await nextTick()
      await updatePosition()
      emit('openTooltip')

      cleanupAutoUpdate = autoUpdate(btnTooltipEl.value!, floatingEl.value!, updatePosition)

      if (timer.value) clearTimeout(timer.value)
      timer.value = setTimeout(() => {
        showTooltip.value = false
      }, props.tooltipTimeout * 1000)
    } else {
      cleanupAutoUpdate?.()
    }
  })

  onBeforeUnmount(() => {
    cleanupAutoUpdate?.()
    if (timer.value) {
      clearTimeout(timer.value)
      timer.value = null
    }
  })
</script>

<template>
  <div v-if="info" class="addon-tooltip">
    <slot name="main-content">
      <span v-if="label">{{ label }}</span>
      <span v-if="amount">{{ amount }}</span>
    </slot>

    <div ref="btnTooltipEl" class="addon-tooltip__info-icon-wrapper">
      <slot name="button" v-bind="{ toggleTooltip }">
        <InfoIcon
          :size="size"
          :color="color"
          class="addon-tooltip__info-icon"
          @click="showTooltip = true"
          @mouseenter="showTooltip = true"
        />
      </slot>
    </div>

    <div v-if="showTooltip" ref="floatingEl" class="addon-tooltip__popup">
      <slot name="tooltip-content">
        <p>{{ info }}</p>
      </slot>
      <div ref="arrowEl" class="addon-tooltip__arrow" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/shared/assets/styles/utils';

  .addon-tooltip {
    display: flex;
    align-items: center;
    gap: 2px;
    color: $text-color-middle;

    @include caption-6;

    width: fit-content;

    &__info-icon-wrapper {
      display: flex;
      align-items: center;
    }

    :deep(.addon-tooltip__info-icon) {
      cursor: pointer;

      path {
        fill-opacity: 0.4;
      }
    }

    &__popup {
      color: $text-color-hight;
      padding: 8px;
      border-radius: 8px;
      background: $white;
      box-shadow:
        0 1px 3px 1px rgb(33 14 86 / 16%),
        0 6px 24px 0 rgb(33 14 86 / 12%);
      z-index: 100;
      max-width: 319px;
      text-align: center;
      position: absolute;

      @include mobile-flow {
        max-width: 250px;
      }
    }

    &__arrow {
      position: absolute;
      width: 12px;
      height: 12px;
      background: $white;
      transform: rotate(45deg);
      z-index: -1;
    }
  }
</style>
